<template>
  <el-dialog
    id="edit-modal"
    class="form-modal"
    :visible.sync="editVisible"
    v-loading="editProcessing"
    :fullscreen="true"
    :show-close="false"
    :modal="false"
  >
    <div id="edit-modal-header" class="modal-header">
      <div class="modal-header-layout flex grid-6 align-center">
        <h1 class="modal-title col-2">View / Edit Time of Day</h1>
        <div class="modal-actions col-2">
          <el-button type="primary" @click="editOperation">Save</el-button>
          <el-button
            type="default"
            class="hidden-xs-only"
            @click="cancelOperation"
            >Cancel</el-button
          >
          <el-button
            type="default"
            icon="el-icon-close"
            class="hidden-sm-and-up"
            @click="cancelOperation"
          />
        </div>
      </div>
    </div>

    <el-form>
      <recent-audits service="Time of Day" :record="selectedTimeOfDay" />
      <el-tabs v-model="activeTab">
        <el-tab-pane label="General" name="General">
          <div class="inline-form-group">
            <el-col :span="11">
              <el-input v-model="editForm.name" placeholder="Name">
                <template slot="prefix">Name</template>
              </el-input>
            </el-col>
            <el-col :span="1" />
            <el-col :span="12">
              <dads-select
                v-model="editForm.timezone"
                placeholder="Select a time zone"
                allow-edit
                filterable
              >
                <template slot="prefix">Time zone</template>
                <el-option label="Use account default" value="" />
                <el-option
                  v-for="(timezone, index) in timezones"
                  :key="index"
                  :label="timezone.name"
                  :value="timezone.name"
                />
              </dads-select>
            </el-col>
          </div>
          <el-select
            v-model="editForm.type"
            @change="hasUserChangedCustomType = true"
            placeholder="Select type"
          >
            <template slot="prefix">Type</template>
            <el-option
              v-for="(item, i) in customTypeOptions"
              :key="i"
              :label="item.label"
              :value="item.value"
            />
          </el-select>

          <div class="inline-form-group">
            <el-col :span="11">
              <dads-select
                v-model="editForm.branchId"
                :value="editForm.branchId"
                placeholder="Select branch"
                filterable
              >
                <template slot="prefix">Branch</template>
                <el-option
                  v-for="item in branchOptions"
                  :key="item + getRandomKey()"
                  :label="item.name"
                  :value="item.id"
                />
              </dads-select>
            </el-col>
          </div>

          <!-- ⭐ WEEKTIME ⭐ -->
          <div class="week-time" v-if="customType === 'WEEKTIME'">
            <el-checkbox v-model="editForm.param3">
              Enable checking for calendar exceptions
            </el-checkbox>

            <dads-select
              v-if="param3"
              v-model="timeOfDayId"
              placeholder="Select time of day"
              filterable
              clearable
            >
              <template slot="prefix">Time of Day</template>
              <el-option
                v-for="{ id, name, isPartner } in weekTimeOfDayOptions"
                :key="id"
                :label="`${name}${isPartner ? ' (partner)' : ''}`"
                :value="id"
              />
            </dads-select>

            <h2 class="black-text">Specify business hours</h2>
            <div
              class="inline-form-group"
              v-for="(day, index) in days"
              :key="index"
            >
              <el-col :span="1" style="align-self: start; margin-top: 20px">
                <el-checkbox
                  @change="
                    editForm.customWeekTime[`day${index}`].enabled = $event
                  "
                  :value="editForm.customWeekTime[`day${index}`].enabled"
                />
              </el-col>
              <el-col :span="2" style="align-self: start; margin-top: 25px">
                <h4 class="black-text mt-0">{{ day }}</h4>
              </el-col>
              <el-col :span="1"></el-col>
              <el-col :span="21">
                <multi-time-range-selector
                  :values="editForm.customWeekTime[`day${index}`].values"
                  :disabled="!editForm.customWeekTime[`day${index}`].enabled"
                  @timeRangeSelected="onTimeRangeSelection(index, $event)"
                />
              </el-col>
            </div>
          </div>

          <!-- ⭐ CALENDAR ⭐ -->
          <div v-if="customType === 'CALENDAR'">
            <h2 class="form-section-header">Select date ranges</h2>
            <div class="mt-10">
              <div
                class="date-picker inline-form-group"
                v-for="(item, i) in dateRanges"
                :key="i"
              >
                <el-col :span="21">
                  <el-date-picker
                    :clearable="true"
                    v-model="dateRanges[i]"
                    type="daterange"
                    format="yyyy-MM-dd"
                    align="right"
                    unlink-panels
                    range-separator="To"
                    start-placeholder="Start date"
                    end-placeholder="End date"
                    @input="onDateRangeSelection($event, i)"
                  />
                </el-col>
                <el-col :span="1" />
                <el-col :span="2">
                  <el-tooltip
                    class="item"
                    effect="dark"
                    :content="`Remove date range ${i + 1}`"
                    placement="top"
                  >
                    <el-button
                      @click="removeDateRange(i)"
                      class="table-action-button icon-red"
                      icon="el-icon-delete"
                    />
                  </el-tooltip>
                </el-col>
              </div>
            </div>
            <div class="inline-form-group">
              <el-button style="float: right" @click="addDateRange"
                >Add date range</el-button
              >
            </div>
          </div>

          <!-- ⭐ USEGLOBAL ⭐ -->
          <el-select
            v-if="customType === 'USEGLOBAL'"
            v-model="editForm.param1"
            placeholder="Select global"
          >
            <el-option
              v-for="(item, i) in globalConditionOptions"
              :key="i"
              :label="`${item.name}${item.isPartner ? ' (partner)' : ''}`"
              :value="item.id"
            />
            <template slot="prefix">Global</template>
          </el-select>

          <!-- ⭐ ALL or ANY ⭐ -->
          <div v-if="['ALL', 'ANY'].includes(customType)">
            <h2 class="form-section-header">Select conditions</h2>
            <el-select
              v-model="editForm.conditionParams"
              placeholder="Select conditions"
              multiple
              filterable
            >
              <template slot="prefix"
                ><span style="opacity: 0">Conditions</span></template
              >
              <el-option
                v-for="(item, i) in conditionOptions"
                :key="i"
                :label="item.name"
                :value="item.id"
              />
            </el-select>
          </div>
          <div v-if="editForm.dids.length">
            <h2 class="form-section-header">Assigned numbers</h2>
            <el-select
              v-model="editForm.dids"
              placeholder="Select numbers"
              multiple
              :disabled="true"
            >
              <template slot="prefix"
                ><span style="opacity: 0">Numbers</span></template
              >
              <el-option
                v-for="(item, i) in selectedDidOptions"
                :key="i"
                :label="item.number | formatNumber"
                :value="item.id"
              />
            </el-select>
            <el-tag class="mb-10">
              <i class="el-icon-info" style="margin-right: 4px"></i>
              You can modify this in the phone numbers section
            </el-tag>
          </div>
        </el-tab-pane>
        <el-tab-pane label="Routing" name="Routing" v-if="customType !== ''">
          <div>
            <!-- ⭐ DESTINATION WHEN MATCHES ⭐ -->
            <h2 v-if="customType === 'WEEKTIME'" class="form-section-header">
              During business hours
            </h2>
            <h2 v-else class="form-section-header">If condition matches</h2>
            <destination-selector
              :isTerminalDestSelector="true"
              :draggable="true"
              :showHeader="false"
              :draggableDestTypes="draggableDestTypes"
              :include-pharmacy-dests="true"
                :include-pharmacy-agents="true"
              :selectedDestinations="editForm.matchDestinations"
              @destinationsSelected="onMatchDestinationsSelection"
              title=""
              :clearFields="clearFormFields"
            />

            <!-- ⭐ DESTINATION WHEN NOT MATCHES ⭐ -->
            <h2 v-if="customType === 'WEEKTIME'" class="form-section-header">
              Outside business hours
            </h2>
            <h2 v-else class="form-section-header">
              If condition does not match
            </h2>
            <destination-selector
              :isTerminalDestSelector="true"
              :draggable="true"
              :showHeader="false"
              :draggableDestTypes="draggableDestTypes"
              :include-pharmacy-dests="true"
                :include-pharmacy-agents="true"
              :selectedDestinations="editForm.notMatchDestinations"
              @destinationsSelected="onNotMatchDestinationsSelection"
              title=""
              :clearFields="clearFormFields"
            />
          </div>
        </el-tab-pane>
        <el-tab-pane
          label="Manual Override"
          name="Override"
          v-if="customType === 'WEEKTIME'"
        >
          <h2 class="form-section-header">Override settings</h2>
          <el-checkbox v-model="enableConditionOverride">
            Enable time of day override
          </el-checkbox>
          <el-tag class="mb-1">
            <i class="el-icon-info" style="margin-right: 4px" />
            This allows you to temporarily override your current routing
          </el-tag>
          <div class="inline-form-group">
            <el-col :span="9">
              <h3 class="black-text mb-0">Start date</h3>
              <el-date-picker
                v-model="editForm.param5"
                type="datetime"
                :editable="false"
                placeholder="Select start date"
              >
              </el-date-picker>
            </el-col>
            <el-col :span="1" />
            <el-col :span="9">
              <h3 class="black-text mb-0">End date</h3>
              <el-date-picker
                v-model="editForm.param4"
                type="datetime"
                :editable="false"
                placeholder="Select end date"
              >
              </el-date-picker>
            </el-col>
          </div>
          <h2 class="form-section-header">Destination</h2>
          <destination-selector
            :isTerminalDestSelector="true"
            :draggable="true"
            :showHeader="false"
            :draggableDestTypes="draggableDestTypes"
              :include-pharmacy-dests="true"
                :include-pharmacy-agents="true"
            :selectedDestinations="editForm.overrideDestinations"
            @destinationsSelected="onOverrideDestinationsSelection"
            title=""
            :clearFields="clearFormFields"
          />
        </el-tab-pane>
      </el-tabs>
    </el-form>
    <destination-references
      :tenantId="selectedTimeOfDay ? selectedTimeOfDay.tenantId : null"
      type="CONDITION"
      :id="selectedTimeOfDay ? selectedTimeOfDay.id : null"
      :clearState="clearFormFields"
    />
  </el-dialog>
</template>

<script>
import OmniaTimeSelect from "@/components/OmniaTimeSelect.vue";
import MultiTimeRangeSelector from "./multiTimeRangeSelector.vue";
import DestinationSelector from "@/components/DestinationSelector.vue";
import DadsSelect from "@/components/select";
import moment from "moment";
import DestinationReferences from "@/components/DestinationReferences";
import RecentAudits from "@/components/RecentAudits";
import feathersClient from "@/feathers/feathers-client";

export default {
  name: "TimeOfDaysEditForm",

  props: {
    editVisible: { required: true },
    clearFormFields: { required: true },
    editProcessing: { required: true },
    timezones: { required: true },
    customTypeOptions: { required: true },
    globalConditionOptions: { required: true },
    conditionOptions: { required: true },
    selectedTimeOfDay: { required: true },
    draggableDestTypes: { required: true },
    weekTimeOfDayOptions: { required: true },
    branchOptions: { required: true },
  },

  components: {
    OmniaTimeSelect,
    DestinationSelector,
    DadsSelect,
    DestinationReferences,
    RecentAudits,
    MultiTimeRangeSelector,
  },

  data() {
    return {
      hasUserChangedCustomType: false,
      selectedDidOptions: [],
      activeTab: "General",

      days: ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"],

      enableConditionOverride: false,

      timeOfDayId: null,
      editForm: {
        tenantId: null,
        name: "",
        timezone: "",
        type: "",
        param1: "",
        param3: "",
        param4: "",
        param5: "",
        conditionParams: [],
        customWeekTime: {
          day0: {
            enabled: false,
            values: [],
          },
          day1: {
            enabled: false,
            values: [],
          },
          day2: {
            enabled: false,
            values: [],
          },
          day3: {
            enabled: false,
            values: [],
          },
          day4: {
            enabled: false,
            values: [],
          },
          day5: {
            enabled: false,
            values: [],
          },
          day6: {
            enabled: false,
            values: [],
          },
        },
        matchDestinations: [],
        notMatchDestinations: [],
        overrideDestinations: [],
        dids: [],
        dateRanges: [],
        branchId: null,
      },
      dateRanges: [],
    };
  },

  computed: {
    selectedTenant() {
      return this.$store.state.selectedTenant;
    },

    customType() {
      return this.editForm.type;
    },

    param3() {
      return this.editForm.param3;
    },
  },

  watch: {
    param3(checked) {
      if (!checked) {
        this.timeOfDayId = null;
      }
    },

    customType(val) {
      if (this.hasUserChangedCustomType) {
        if (val !== "USEGLOBAL") {
          this.editForm.param1 = "";
        }

        if (val !== "CALENDAR") {
          this.dateRanges = [[moment(), moment()]];
          this.editForm.dateRanges = [];
        }

        if (val !== "ALL" || val !== "ANY") {
          this.editForm.conditionParams = [];
        }

        if (val !== "CUSTOMWEEKTIME") {
          this.editForm.customWeekTime = {
            day0: {
              enabled: false,
              values: [],
            },
            day1: {
              enabled: false,
              values: [],
            },
            day2: {
              enabled: false,
              values: [],
            },
            day3: {
              enabled: false,
              values: [],
            },
            day4: {
              enabled: false,
              values: [],
            },
            day5: {
              enabled: false,
              values: [],
            },
            day6: {
              enabled: false,
              values: [],
            },
          };
        }
      }
    },

    clearFormFields(value) {
      if (value) {
        this.clearFields();
      }
    },

    selectedTimeOfDay(editTimeOfDay) {
      if (editTimeOfDay) {
        const {
          tenantId,
          name,
          timezone,
          type,
          param1,
          param3,
          param4,
          param5,
          overrideDestinations,
          matchDestinations,
          notMatchDestinations,
          customWeekTime,
          dids,
          dateRanges,
          branchId,
        } = editTimeOfDay;

        this.editForm.tenantId = tenantId;
        this.editForm.name = name;
        this.editForm.timezone = timezone;
        this.editForm.type = type;
        this.editForm.param1 = param1 !== "" ? Number(param1) : "";

        // ⭐️ Populate `Time of Day` for WEEKTIME ⭐️ //
        if (type === "WEEKTIME" && param3) {
          this.timeOfDayId = +param3;
        }
        this.editForm.param3 = !!param3;

        //override settings
        this.enableConditionOverride = !!param4;
        this.editForm.param4 = param4;
        this.editForm.param5 = param5;

        if (type === "WEEKTIME" && customWeekTime) {
          Object.keys(customWeekTime).forEach((day) => {
            this.editForm.customWeekTime[day] = { ...customWeekTime[day] };
          });
        }

        if (type === "ALL" || type === "ANY") {
          this.editForm.conditionParams = [...editTimeOfDay.conditionParams];
        }

        if (type === "CALENDAR") {
          if (dateRanges && dateRanges.length > 0) {
            dateRanges.forEach(({ startDate, endDate }) => {
              this.dateRanges.push([
                moment(new Date(startDate)).format("YYYY-MM-DD HH:mm:ss"),
                moment(new Date(endDate)).format("YYYY-MM-DD HH:mm:ss"),
              ]);
            });
            this.editForm.dateRanges = this.dateRanges.map(
              ([startDate, endDate]) => {
                return {
                  startDate,
                  endDate,
                };
              }
            );
          } else {
            this.dateRanges.push([moment(), moment()]);
          }
        } else {
          this.dateRanges.push([moment(), moment()]);
        }

        this.editForm.matchDestinations = [...matchDestinations];
        this.editForm.notMatchDestinations = [...notMatchDestinations];
        this.editForm.overrideDestinations = [...overrideDestinations];

        if (dids) {
          const { value, didOptions } = dids;
          this.editForm.dids = [...value];
          this.selectedDidOptions = didOptions;
        }
        this.setBranchId(branchId);
      }
    },
  },

  methods: {
    onTimeRangeSelection(index, ranges) {
      this.editForm.customWeekTime[`day${index}`].values = [...ranges];
    },

    onDateRangeSelection($event, idx) {
      const startDate = moment($event[0]).format("YYYY-MM-DD HH:mm:ss");
      const endDate = moment($event[1]).format("YYYY-MM-DD HH:mm:ss");
      this.editForm.dateRanges[idx] = {
        startDate,
        endDate,
      };
    },
    getRandomKey() {
      const randomString = () => Math.random().toString(36).slice(2);
      return `${randomString()}-${randomString()}$${randomString()}`;
    },
    async setBranchId(branchId) {
      if (branchId && branchId !== 0) {
        this.editForm.branchId = branchId;
        return;
      }
      try {
        const result = await feathersClient.service("branches").find({
          query: { tenantId: this.selectedTenant.id, default: 1 },
        });
        this.editForm.branchId = result.length > 0 ? result[0].id : null;
      } catch (error) {
        console.error("Failed to fetch the branch", error);
        this.editForm.branchId = null;
      }
    },

    addDateRange() {
      this.dateRanges.push([moment(), moment()]);
    },

    removeDateRange(idx) {
      this.dateRanges.splice(idx, 1);
      this.editForm.dateRanges.splice(idx, 1);
      if (this.dateRanges.length === 0) {
        this.dateRanges.push([moment(), moment()]);
      }
    },

    onMatchDestinationsSelection(destinations) {
      this.editForm.matchDestinations = [...destinations];
    },

    onNotMatchDestinationsSelection(destinations) {
      this.editForm.notMatchDestinations = [...destinations];
    },

    onOverrideDestinationsSelection(destinations) {
      this.editForm.overrideDestinations = [...destinations];
    },

    validateDestinations(destinations, type) {
      if (destinations.length > 1 && destinations.find((dst) => !dst.id)) {
        this.$notify({
          title: `Empty ${type} Destination field`,
          message: "Don't leave any destination field to be empty.",
          type: "error",
        });
        return;
      }

      let finalDestinations = [];
      if (destinations.length === 1 && destinations[0].id === null) {
        return finalDestinations;
      }

      if (destinations.length) {
        destinations.forEach((dst, idx) => (dst.order = idx + 1));

        finalDestinations = destinations.map(({ id, type, order }) => ({
          id,
          type,
          order,
        }));
      }
      return finalDestinations;
    },

    editOperation() {
      this.editForm.tenantId = this.selectedTenant.id;

      const editBody = { ...this.editForm };

      if (this.param3 && this.timeOfDayId && editBody.type === "WEEKTIME") {
        editBody.param3 = `${this.timeOfDayId}`;
      } else {
        editBody.param3 = "";
      }
      this.editForm.branchId = this.editForm.branchId || null;

      const matchDestinations = this.validateDestinations(
        editBody.matchDestinations,
        "MATCH"
      );
      const notMatchDestinations = this.validateDestinations(
        editBody.notMatchDestinations,
        "NOT-MATCH"
      );
      const overrideDestinations = this.validateDestinations(
        editBody.overrideDestinations,
        "OVERRIDE"
      );

      if (this.enableConditionOverride) {
        if (!editBody.param4) {
          this.$notify({
            title: `Invalid End`,
            message: "End date must not be empty",
            type: "error",
          });
          return;
        }
        if (!editBody.param5) {
          this.$notify({
            title: `Invalid Start`,
            message: "Start date must not be empty",
            type: "error",
          });
          return;
        }
        editBody.param4 = moment(editBody.param4).format("YYYY/MM/DD HH:mm:ss");
        editBody.param5 = moment(editBody.param5).format("YYYY/MM/DD HH:mm:ss");

        if (editBody.param5 > editBody.param4) {
          this.$notify({
            title: `Invalid Dates`,
            message: "Start date should not be later than end date",
            type: "error",
          });
          return;
        }
      } else {
        editBody.param4 = "";
        editBody.param5 = "";
      }

      if (matchDestinations && notMatchDestinations && overrideDestinations) {
        editBody.matchDestinations = matchDestinations;
        editBody.notMatchDestinations = notMatchDestinations;
        editBody.overrideDestinations = overrideDestinations;
      } else {
        return;
      }

      this.$emit("editOperation", editBody);
    },
    async getDefaultBranch() {
      try {
        const [defaultBranch] = await feathersClient.service("branches").find({
          query: {
            tenantId: this.selectedTenant.id,
            default: 1,
          },
        });
        this.editForm.branchId = defaultBranch.id;
      } catch (error) {
        console.log(error);
      }
    },

    cancelOperation() {
      this.$emit("cancelOperation", "edit");
    },

    clearFields() {
      this.dateRanges = [];
      this.hasUserChangedCustomType = false;
      this.selectedDidOptions = [];
      this.activeTab = "General";
      this.enableConditionOverride = false;

      this.timeOfDayId = null;
      this.editForm = {
        tenantId: null,
        name: "",
        timezone: "",
        type: "",
        param1: "",
        param3: "",
        param4: "",
        param5: "",
        conditionParams: [],
        customWeekTime: {
          day0: {
            enabled: false,
            values: [],
          },
          day1: {
            enabled: false,
            values: [],
          },
          day2: {
            enabled: false,
            values: [],
          },
          day3: {
            enabled: false,
            values: [],
          },
          day4: {
            enabled: false,
            values: [],
          },
          day5: {
            enabled: false,
            values: [],
          },
          day6: {
            enabled: false,
            values: [],
          },
        },
        matchDestinations: [],
        notMatchDestinations: [],
        overrideDestinations: [],
        dids: [],
        dateRanges: [],
        branchId: null,
      };
      this.getDefaultBranch();
    },

    remoteSearchDids(query) {
      if (query && query.trim().length) {
        this.$emit("remoteSearchDids", query);
      }
    },
    created() {
      this.getDefaultBranch();
    },
  },

  filters: {
    formatNumber(value) {
      return `(${value.substr(0, 3)}) ${value.substr(3, 3)}-${value.substr(
        6,
        4
      )}`;
    },
  },
};
</script>

<style lang="scss" scoped>
@import "../../../../assets/scss/variables";

.el-date-editor {
  width: 100%;
}

.date-picker {
  margin-bottom: 1.5rem;
}

.inline-form-group .el-checkbox {
  margin-left: 0;
}

.el-checkbox {
  margin-left: 0;
}

.el-tabs {
  max-width: 600px;
  margin: 0 auto;

  h4 {
    margin-bottom: 0;
  }
}
</style>
